import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Award, ShieldCheck, Zap, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Import Hero assets
import Image1 from "../Assets/Home/cranes.jpg";
import Image2 from "../Assets/Home/image2.jpg";
import Image3 from "../Assets/Home/crane_1.png";
import Image4 from "../Assets/Home/Home_1.jpg";
import Image5 from "../Assets/Home/Home_2.avif";

import Header from '../Components/Header';
import Footer from '../Components/Footer';

const slides = [
  {
    id: 1,
    image: Image1,
    title: "RETHINKING LIFTING",
    subtitle: "Precision Engineering for Industrial Excellence.",
    description: "Leading manufacturers of high-end truck mounted cranes and industrial lifting solutions."
  },
  {
    id: 2,
    image: Image2,
    title: "ENGINEERED FOR SAFETY",
    subtitle: "Innovation Meets Reliability.",
    description: "Specialized in customized hydraulic solutions and heavy-duty gantry systems."
  }
];

const Home = () => {
  const [index, setIndex] = useState(0);
  
  // --- BACKEND STATE ---
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % slides.length), 5000);
    document.title = "Marvel Sato | Home";
    // FETCH DATA
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/bookings/categories`); 
        setCategories(response.data);
        if (response.data.length > 0) {
          setActiveTab(response.data[0]); // Set first category as default
        }
        setLoading(false);
      } catch (error) {
        console.error("Error Fetching Categories", error);
        setLoading(false);
      }
    };

    fetchCategories();
    return () => clearInterval(timer);
  }, []);

  // Animation Variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 1.2, ease: "easeOut" }
  };

  return (
    <div className="bg-white overflow-x-hidden w-full">
      <Header />
      
      {/* 1. HERO SECTION */}
      <section id="home" className="relative h-[100svh] w-full overflow-hidden bg-slate-900">
        <AnimatePresence mode="wait">
          <motion.div
            key={slides[index].id}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div className="absolute inset-0 bg-black/40 z-10" />
            <img src={slides[index].image} alt="Hero" className="h-full w-full object-cover" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-20 h-full flex flex-col justify-center px-6 sm:px-12 md:px-24 max-w-7xl mx-auto text-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={slides[index].id}
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="max-w-3xl"
            >
              <h4 className="text-orange-500 font-bold tracking-widest mb-4 uppercase text-sm md:text-base">{slides[index].subtitle}</h4>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black mb-6 leading-tight">{slides[index].title}</h1>
              <p className="text-slate-200 text-base md:text-lg mb-8 max-w-2xl">{slides[index].description}</p>
              <Link to="/products"><button className="bg-orange-500 hover:bg-white hover:text-orange-600 px-6 py-3 md:px-8 md:py-4 rounded-lg font-bold transition-all shadow-lg shadow-orange-500/20 text-sm md:text-base">
                EXPLORE
              </button></Link>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* 2. ABOUT US */}
      <section 
        id="about" 
        className="py-16 md:py-24 px-4 sm:px-8 md:px-24 relative bg-slate-100 bg-fixed"
        style={{ 
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${Image4})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <motion.div 
            {...fadeInUp}
            style={{ 
                backgroundImage: `linear-gradient(rgba(248, 250, 252, 0.95), rgba(248, 250, 252, 0.95)), url(${Image5})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
            className="max-w-7xl mx-auto rounded-2xl md:rounded-[3rem] p-6 sm:p-10 md:p-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center border border-slate-200 shadow-xl overflow-hidden relative z-10"
        >
          <motion.div 
            initial={{ opacity: 0, x: -60 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: false }}
            transition={{ duration: 1, delay: 0.2 }}
            className="order-2 lg:order-1"
          >
            <h4 className="text-orange-500 font-bold mb-2 uppercase tracking-tight text-sm">Who We Are</h4>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4 uppercase">Lifting Equipment And Services People Can Trust</h2>
            <p className="text-slate-600 leading-relaxed mb-6 text-sm md:text-lg">
              Marvel Engineering Private Limited is a startup specializing in Truck Mounted Cranes. We cater to modern industrial needs by designing and manufacturing the most advanced industrial equipment.
            </p>
            <ul className="space-y-3 md:space-y-4 mb-8">
              {[
                { text: 'Certified ISO 9001:2015', icon: <Award size={18}/> },
                { text: 'Expert Technical Support', icon: <Zap size={18}/> },
                { text: 'Global Delivery Systems', icon: <ShieldCheck size={18}/> }
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-slate-800 font-bold group text-sm md:text-base">
                  <span className="text-orange-500 bg-white p-2 rounded-lg group-hover:bg-orange-500 group-hover:text-white transition-colors shadow-sm shrink-0">{item.icon}</span> 
                  {item.text}
                </li>
              ))}
            </ul>
              <Link to="/about">
                <button className="group text-slate-900 font-bold border-b-2 border-orange-500 hover:text-orange-500 transition-all flex items-center gap-2 pb-1 w-fit">
                  MORE ABOUT US <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform"/>
                </button>
              </Link>
          </motion.div>
          
          <div className="relative flex flex-col items-center justify-center order-1 lg:order-2">
            <h2 className="text-[60px] sm:text-[100px] md:text-[120px] lg:text-[150px] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-cover bg-center select-none italic"
                style={{ backgroundImage: `url(${Image1})`, WebkitBackgroundClip: 'text', textShadow: '2px 2px 10px rgba(0,0,0,0.1)' }}>
              MARVEL
            </h2>
            
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false }}
              transition={{ duration: 1.2, delay: 0.5, type: "spring", bounce: 0.3 }}
              className="mt-[-20px] sm:mt-[-40px] z-10 w-full flex justify-center"
            >
              <img 
                src={Image3} 
                alt="Engineering Truck" 
                className="w-full max-w-[280px] sm:max-w-[380px] md:max-w-[450px] drop-shadow-[0_25px_35px_rgba(0,0,0,0.15)]"
              />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 3. CAPACITY RANGE (DYNAMIC) */}
      <section id="products" className="bg-slate-50 py-12 md:py-20 px-4 sm:px-8 md:px-24 overflow-hidden min-h-fit lg:min-h-screen flex flex-col justify-center">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center mb-8 md:mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-2 uppercase">Capacity Range</h2>
            <div className="h-1 w-20 bg-orange-500 mx-auto rounded-full"></div>
          </motion.div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin text-orange-500" size={48} />
            </div>
          ) : (
            <>
<div className="flex flex-wrap justify-center gap-1.5 md:gap-3 mb-8">
  {categories.map((cat) => (
    <button
      key={cat._id}
      onClick={() => setActiveTab(cat)}
      className={`px-3 md:px-5 py-1.5 md:py-2 rounded-full font-black transition-all duration-500 text-[10px] md:text-xs uppercase tracking-wider ${
        activeTab?._id === cat._id 
        ? "bg-slate-900 text-white shadow-lg scale-105" 
        : "bg-white text-slate-400 hover:bg-orange-50 hover:text-orange-500 shadow-sm border border-slate-100"
      }`}
    >
      {cat.categoryName}
    </button>
  ))}
</div>

              {activeTab && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 bg-white p-6 md:p-10 rounded-2xl md:rounded-3xl shadow-sm border border-slate-100 relative items-center">
                  <motion.div 
                    initial={{ x: -100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-[250px] sm:h-[350px] lg:h-[400px] rounded-2xl overflow-hidden shadow-lg border-4 border-slate-50 order-1"
                  >
                    <AnimatePresence mode="wait">
                      <motion.img 
                        key={activeTab._id}
                        src={activeTab.imageId} // Now uses the URL String from DB
                        alt={activeTab.categoryName} 
                        initial={{ opacity: 0, scale: 1.05 }} 
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.4 }} 
                        className="w-full h-full object-cover" 
                      />
                    </AnimatePresence>
                  </motion.div>

                  <motion.div 
                    initial={{ x: 100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="flex flex-col justify-center order-2"
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab._id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-3 uppercase italic tracking-tighter">
                          {activeTab.categoryName} <span className="text-orange-500">Series</span>
                        </h3>
                        <p className="text-slate-500 mb-6 text-sm md:text-base leading-relaxed">
                          {activeTab.categoryDescription}
                        </p>
                        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
                          {activeTab.features.map((f, i) => (
                            <div key={i} className="bg-slate-50 p-3 rounded-xl border-l-4 border-orange-500">
                              <p className="text-[10px] md:text-xs text-slate-400 uppercase font-bold mb-1">{f.specType}</p>
                              <p className="text-sm md:text-base font-black text-slate-900 tracking-tight">{f.specValue}</p>
                            </div>
                          ))}
                        </div>
                        <Link 
  to={`/category/${activeTab._id}`} 
  className="block w-full sm:w-fit"
>
  <button className="flex items-center gap-2 bg-slate-900 text-white w-full justify-center px-8 py-3 rounded-lg font-bold hover:bg-orange-500 transition-colors shadow-lg text-sm md:text-base">
    VIEW FULL SPECS <ChevronRight size={18} />
  </button>
</Link>
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* 4. BROCHURE DOWNLOAD */}
      <section className="relative min-h-[500px] md:min-h-[600px] lg:min-h-[700px] flex items-center px-6 sm:px-12 md:px-24 bg-[#e65a0a] overflow-hidden">
        <div className="absolute right-0 top-0 w-full md:w-1/2 h-full">
              <img src={Image2} className="w-full h-full object-cover object-center opacity-40 md:opacity-100" alt="Crane" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#e65a0a] via-[#1e40af]/30 to-transparent"></div>
        </div>

        <div className="relative z-20 max-w-4xl text-white">
          <motion.div 
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-black leading-tight">
              Comprehensive Lifting <br className="hidden sm:block" /> Solutions In The <br className="hidden sm:block" /> Right Platform
            </h2>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-base md:text-lg font-medium">
                <span className="text-orange-500 text-xl md:text-2xl font-bold">›</span> Ensure optimal efficiency and safety
              </li>
              <li className="flex items-center gap-3 text-base md:text-lg font-medium">
                <span className="text-orange-500 text-xl md:text-2xl font-bold">›</span> Cutting-edge functionalities
              </li>
            </ul>
           <a href="sato.pdf" download="Marvel_sato_brochure.pdf">
            <button className="mt-4 bg-white text-blue-900 px-8 md:px-10 py-3 md:py-4 rounded font-bold text-sm md:text-lg hover:bg-orange-500 hover:text-white transition-all shadow-xl">
              Download Brochure
            </button></a>
          </motion.div>
        </div>
      </section>

      {/* 5. LOCATE US */}
      <section className="py-16 md:py-24 px-4 sm:px-8 md:px-24 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: false, amount: 0.2 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="grid grid-cols-1 lg:grid-cols-2 bg-slate-900 rounded-2xl md:rounded-[3rem] overflow-hidden shadow-2xl"
        >
          <div className="p-8 sm:p-12 md:p-16 flex flex-col justify-center">
            <MapPin size={40} className="text-orange-500 mb-6" />
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6 italic tracking-tighter uppercase">Locate Our Unit</h2>
            <p className="text-slate-400 mb-10 leading-relaxed text-sm md:text-lg">
              Our state-of-the-art manufacturing facility is located in the Industrial Belt of Kerala.
            </p>
            <div className="space-y-6">
              <div className="border-l-2 border-orange-500 pl-4">
                <p className="text-slate-500 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-1">Corporate Office</p>
                <p className="text-white font-bold text-lg md:text-xl">Bellari Road, Hosepate, Karnataka</p>
              </div>
              <div className="border-l-2 border-orange-500 pl-4">
                <p className="text-slate-500 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-1">Contact Support</p>
                <p className="text-white font-bold text-lg md:text-xl">+91 9449 826754</p>
              </div>
            </div>
          </div>
          <div className="h-[300px] sm:h-[400px] lg:h-full min-h-[350px] w-full bg-slate-800">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2961.910860114459!2d76.38828567369842!3d15.26884706037002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb9d5000a1b898f%3A0x824903136e8a437e!2sMarvel%20Enterprise!5e1!3m2!1sen!2sin!4v1768972740163!5m2!1sen!2sin" 
                  className="w-full h-full border-0" 
                  allowFullScreen="" 
                  loading="lazy"
                  title="Marvel Location"
                ></iframe>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
};

export default Home;